/**
 * Minimized URL Panels Store
 *
 * Zustand store for managing minimized URL preview panels.
 * These panels persist at the AppShell level and are accessible from ANY page.
 *
 * Features:
 * - Add/remove minimized URL panels
 * - Restore panels as global overlays
 * - Track panel metadata (title, URL, source weekId)
 * - Dock visibility toggle
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Minimized URL panel data structure
 */
export interface MinimizedUrlPanel {
  /** Unique panel ID */
  id: string;
  /** Display title for the panel */
  title: string;
  /** Custom user-defined title (overrides default title when set) */
  customTitle?: string;
  /** URL to preview */
  url: string;
  /** Optional source week ID (for Weekly Board context) */
  weekId?: string;
  /** Timestamp when minimized */
  minimizedAt: number;
  /** Whether this panel uses remote browser (external URL with server-side Chromium) */
  isRemote?: boolean;
}

/**
 * State for a restored (expanded) panel
 */
export interface RestoredPanelState {
  /** Panel ID */
  id: string;
  /** Display title */
  title: string;
  /** Custom user-defined title (overrides default title when set) */
  customTitle?: string;
  /** URL to preview */
  url: string;
  /** Whether the panel is in fullscreen mode */
  isFullscreen: boolean;
  /** Panel width percentage (for resizable mode) */
  panelWidth: number;
  /** Whether this panel uses remote browser (external URL with server-side Chromium) */
  isRemote?: boolean;
  /**
   * Whether the panel is visually minimized but session preserved.
   * When true for remote browser panels:
   * - Panel is hidden via CSS (not unmounted)
   * - Browser session remains active
   * - Frames may be throttled to save CPU
   */
  isVisuallyMinimized?: boolean;
}

interface MinimizedUrlPanelsState {
  /** List of minimized URL panels */
  minimizedPanels: MinimizedUrlPanel[];

  /** Currently restored (expanded) panel, if any */
  restoredPanel: RestoredPanelState | null;

  /** Whether the dock is visible */
  isDockVisible: boolean;

  // Actions
  /** Minimize a URL panel */
  minimizePanel: (panel: Omit<MinimizedUrlPanel, 'id' | 'minimizedAt'>) => string;

  /** Restore a panel from minimized state (opens as overlay) */
  restorePanel: (id: string) => void;

  /** Close the restored panel (fully close, not minimize) */
  closeRestoredPanel: () => void;

  /** Re-minimize the currently restored panel */
  reMinimizeRestoredPanel: () => void;

  /** Restore a visually minimized panel (used for remote browser sessions) */
  restoreVisuallyMinimizedPanel: () => void;

  /** Remove a minimized panel entirely */
  removePanel: (id: string) => void;

  /** Toggle dock visibility */
  toggleDockVisibility: () => void;

  /** Set dock visibility */
  setDockVisible: (visible: boolean) => void;

  /** Set fullscreen mode for restored panel */
  setRestoredPanelFullscreen: (isFullscreen: boolean) => void;

  /** Set panel width for restored panel */
  setRestoredPanelWidth: (width: number) => void;

  /** Rename a minimized panel's custom title */
  renamePanelTitle: (id: string, customTitle: string) => void;

  /** Rename the restored panel's custom title */
  renameRestoredPanelTitle: (customTitle: string) => void;

  /** Clear all minimized panels */
  clearAllPanels: () => void;
}

/**
 * Generate UUID for panels
 */
const generateId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/** Default panel width percentage */
const DEFAULT_PANEL_WIDTH = 66;

/**
 * Minimized URL Panels Zustand Store
 *
 * Persists minimized panels to localStorage so they survive page navigation and refresh.
 * The restoredPanel is NOT persisted since it's transient (expanded overlay state).
 */
export const useMinimizedUrlPanelsStore = create<MinimizedUrlPanelsState>()(
  persist(
    (set, get) => ({
      minimizedPanels: [],
      restoredPanel: null,
      isDockVisible: true,

  minimizePanel: (panel) => {
    const id = generateId();
    const now = Date.now();

    const newPanel: MinimizedUrlPanel = {
      ...panel,
      id,
      minimizedAt: now,
    };

    set((state) => ({
      minimizedPanels: [...state.minimizedPanels, newPanel],
    }));

    return id;
  },

  restorePanel: (id) => {
    const { minimizedPanels, restoredPanel } = get();
    const panel = minimizedPanels.find((p) => p.id === id);

    if (!panel) return;

    // Check if this is restoring a visually minimized remote browser panel
    // (the panel is still mounted but hidden, just needs to be shown)
    if (restoredPanel?.isVisuallyMinimized && restoredPanel.id === id) {
      // Just un-hide the already-mounted panel
      set((state) => ({
        minimizedPanels: state.minimizedPanels.filter((p) => p.id !== id),
        restoredPanel: {
          ...state.restoredPanel!,
          isVisuallyMinimized: false,
        },
      }));
      return;
    }

    // Remove from minimized list and set as restored
    set((state) => ({
      minimizedPanels: state.minimizedPanels.filter((p) => p.id !== id),
      restoredPanel: {
        id: panel.id,
        title: panel.title,
        customTitle: panel.customTitle,
        url: panel.url,
        isFullscreen: false,
        panelWidth: DEFAULT_PANEL_WIDTH,
        isRemote: panel.isRemote,
        isVisuallyMinimized: false,
      },
    }));
  },

  closeRestoredPanel: () => {
    set({ restoredPanel: null });
  },

  reMinimizeRestoredPanel: () => {
    const { restoredPanel } = get();
    if (!restoredPanel) return;

    // For remote browser panels, preserve the session by keeping component mounted
    // but visually hidden. This prevents session disconnect on minimize.
    if (restoredPanel.isRemote) {
      // Add to minimized list for dock display
      const minimizedPanel: MinimizedUrlPanel = {
        id: restoredPanel.id,
        title: restoredPanel.title,
        customTitle: restoredPanel.customTitle,
        url: restoredPanel.url,
        minimizedAt: Date.now(),
        isRemote: true,
      };

      set((state) => ({
        minimizedPanels: [...state.minimizedPanels, minimizedPanel],
        // Keep restoredPanel but mark as visually minimized (CSS hide, not unmount)
        restoredPanel: {
          ...state.restoredPanel!,
          isVisuallyMinimized: true,
        },
      }));
      return;
    }

    // For non-remote panels (iframes), use original behavior
    const minimizedPanel: MinimizedUrlPanel = {
      id: restoredPanel.id,
      title: restoredPanel.title,
      customTitle: restoredPanel.customTitle,
      url: restoredPanel.url,
      minimizedAt: Date.now(),
      isRemote: restoredPanel.isRemote,
    };

    set((state) => ({
      minimizedPanels: [...state.minimizedPanels, minimizedPanel],
      restoredPanel: null,
    }));
  },

  restoreVisuallyMinimizedPanel: () => {
    const { restoredPanel, minimizedPanels } = get();
    if (!restoredPanel?.isVisuallyMinimized) return;

    // Remove from minimized list (if present) and show restored panel
    set((state) => ({
      minimizedPanels: state.minimizedPanels.filter((p) => p.id !== restoredPanel.id),
      restoredPanel: {
        ...state.restoredPanel!,
        isVisuallyMinimized: false,
      },
    }));
  },

  removePanel: (id) => {
    set((state) => ({
      minimizedPanels: state.minimizedPanels.filter((p) => p.id !== id),
    }));
  },

  toggleDockVisibility: () => {
    set((state) => ({ isDockVisible: !state.isDockVisible }));
  },

  setDockVisible: (visible) => {
    set({ isDockVisible: visible });
  },

  setRestoredPanelFullscreen: (isFullscreen) => {
    set((state) => {
      if (!state.restoredPanel) return state;
      return {
        restoredPanel: {
          ...state.restoredPanel,
          isFullscreen,
        },
      };
    });
  },

  setRestoredPanelWidth: (width) => {
    set((state) => {
      if (!state.restoredPanel) return state;
      return {
        restoredPanel: {
          ...state.restoredPanel,
          panelWidth: width,
        },
      };
    });
  },

  renamePanelTitle: (id, customTitle) => {
    set((state) => ({
      minimizedPanels: state.minimizedPanels.map((p) =>
        p.id === id ? { ...p, customTitle: customTitle.trim() || undefined } : p
      ),
    }));
  },

  renameRestoredPanelTitle: (customTitle) => {
    set((state) => {
      if (!state.restoredPanel) return state;
      return {
        restoredPanel: {
          ...state.restoredPanel,
          customTitle: customTitle.trim() || undefined,
        },
      };
    });
  },

  clearAllPanels: () => {
    set({ minimizedPanels: [], restoredPanel: null });
  },
    }),
    {
      name: 'bo360-minimized-url-panels',
      version: 1,
      // Only persist minimizedPanels and isDockVisible, not restoredPanel (transient)
      partialize: (state) => ({
        minimizedPanels: state.minimizedPanels,
        isDockVisible: state.isDockVisible,
      }),
    }
  )
);
